import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle, Clock, HelpCircle, ArrowRight, RefreshCw, AlertCircle } from 'lucide-react';

const PaymentResultPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'failed' | 'processing' | 'unknown'>('unknown');
  const [error, setError] = useState('');

  const statusGradientClass = paymentStatus === 'success'
    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
    : paymentStatus === 'failed'
      ? 'bg-gradient-to-r from-destructive to-red-600'
      : paymentStatus === 'processing'
        ? 'bg-gradient-to-r from-blue-500 to-blue-600'
        : 'bg-gradient-to-r from-muted-foreground to-gray-500';

  useEffect(() => {
    if (!user) {
      showAlert({
        type: 'warning',
        message: t('vipPlan.warningLogin'),
      });
      navigate('/login-form');
      return;
    }

    checkPaymentStatus();
  }, [user]);

  const checkPaymentStatus = async () => {
    try {
      setLoading(true);
      setError('');

      // 從 URL 參數中獲取 payment_intent_id 和簽名
      const paymentIntentId = searchParams.get('payment_intent_id') || 
                              searchParams.get('intent_id') ||
                              searchParams.get('id');
      const sign = searchParams.get('sign');

      if (!paymentIntentId) {
        setError(t('paymentResult.errorNoInfo'));
        setPaymentStatus('unknown');
        setLoading(false);
        return;
      }

      // 等待一下讓 webhook 處理
      await new Promise(resolve => setTimeout(resolve, 2000));

      // 查詢支付狀態（帶簽名驗證）
      const response = await apiService.payments.getStatus(paymentIntentId, sign);
      
      if (response.success && response.data) {
        const status = response.data.status;
        
        if (status === 'COMPLETED') {
          setPaymentStatus('success');
          await refreshUser();
          showAlert({
            type: 'success',
            message: t('payment.successPayment'),
          });
          // 3 秒後自動跳轉回主控台
          setTimeout(() => {
            navigate('/home');
          }, 3000);
        } else if (status === 'FAILED') {
          setPaymentStatus('failed');
          setError(t('payment.failedPayment'));
          showAlert({
            type: 'error',
            message: t('payment.failedPayment'),
          });
        } else if (status === 'EXPIRED') {
          setPaymentStatus('failed');
          setError(t('payment.expiredPayment', '此付款已逾時未完成，請重新發起付款'));
          showAlert({
            type: 'error',
            message: t('payment.expiredPayment', '此付款已逾時未完成，請重新發起付款'),
          });
        } else if (status === 'PROCESSING' || status === 'PENDING') {
          setPaymentStatus('processing');
          // 如果還在處理中，繼續檢查
          setTimeout(checkPaymentStatus, 3000);
        } else {
          setPaymentStatus('unknown');
          setError(t('paymentResult.errorUnknown', { status }));
        }
      } else {
        setPaymentStatus('unknown');
        setError(t('paymentResult.errorFailed'));
        showAlert({
          type: 'error',
          message: t('paymentResult.errorFailed'),
        });
      }
    } catch (error: any) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('unknown');
      setError(t('paymentResult.errorException'));
      showAlert({
        type: 'error',
        message: t('paymentResult.errorException'),
      });
      
      // 即使檢查失敗，也嘗試刷新用戶信息（可能 webhook 已經處理）
      await refreshUser();
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-[60vh] flex items-center justify-center">
        <Loading message={t('paymentResult.checking')} />
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen text-foreground p-4 sm:p-8">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">{t('paymentResult.pageTitle', 'Payment Result')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('paymentResult.pageSubtitle', 'Status of your recent transaction.')}</p>
        </div>

        <div className="flex items-center justify-center min-h-[50vh]">
          <Card className="w-full max-w-md shadow-xl border-border rounded-xl overflow-hidden text-center relative">
        <div className={`absolute top-0 left-0 h-1.5 w-full ${statusGradientClass}`} />

        <CardContent className="pt-12 pb-8 px-6 md:px-10">
          {paymentStatus === 'success' && (
            <div className="flex flex-col items-center animate-in fade-in zoom-in duration-500">
              <div className="bg-emerald-50 dark:bg-emerald-950 w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-inner">
                <CheckCircle className="w-14 h-14 text-emerald-500" />
              </div>
              <h2 className="text-3xl font-extrabold text-foreground mb-3">{t('paymentResult.success')}</h2>
              <p className="text-muted-foreground font-medium leading-relaxed max-w-xs mx-auto mb-8">
                {t('paymentResult.successDesc')}
              </p>
              <Button
                onClick={() => navigate('/home')}
                className="w-full py-6 text-lg font-bold bg-emerald-600 hover:bg-emerald-700 shadow-md hover:shadow-lg transition-all text-white"
              >
                {t('paymentResult.goNow')} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}

          {paymentStatus === 'failed' && (
            <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300">
              <div className="bg-destructive/10 w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-inner">
                <XCircle className="w-14 h-14 text-destructive" />
              </div>
              <h2 className="text-3xl font-extrabold text-foreground mb-3">{t('paymentResult.failed')}</h2>
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-xl text-sm font-medium mb-8 max-w-sm flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <span className="text-left leading-relaxed">{error}</span>
                </div>
              )}
              <div className="flex flex-col gap-3 w-full">
                <Button 
                  onClick={() => navigate('/subscription')} 
                  variant="default"
                  className="w-full py-6 text-lg font-bold shadow-md"
                >
                  {t('payment.backToPlans')}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={checkPaymentStatus}
                  className="w-full py-5 font-semibold"
                >
                  <RefreshCw className="mr-2 w-4 h-4" /> {t('paymentResult.recheck')}
                </Button>
              </div>
            </div>
          )}

          {paymentStatus === 'processing' && (
            <div className="flex flex-col items-center animate-in fade-in duration-300">
              <div className="bg-blue-50 dark:bg-blue-950 w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-inner relative">
                <Clock className="w-12 h-12 text-blue-500 animate-pulse" />
                <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-200 dark:border-blue-800 border-t-blue-500 rounded-full animate-spin"></div>
              </div>
              <h2 className="text-2xl font-extrabold text-foreground mb-3">{t('paymentResult.processing')}</h2>
              <p className="text-muted-foreground font-medium leading-relaxed mb-8">
                {t('paymentResult.processingDesc')}
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/subscription')}
                className="w-full py-5"
              >
                {t('payment.backToPlans')}
              </Button>
            </div>
          )}

          {paymentStatus === 'unknown' && (
            <div className="flex flex-col items-center animate-in fade-in duration-300">
              <div className="bg-amber-50 dark:bg-amber-950 w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-inner">
                <HelpCircle className="w-14 h-14 text-amber-500" />
              </div>
              <h2 className="text-2xl font-extrabold text-foreground mb-3">{t('paymentResult.unknown')}</h2>
              {error && (
                <div className="bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-200 px-4 py-3 rounded-xl text-sm font-medium mb-8 max-w-sm text-left">
                  {error}
                </div>
              )}
              <div className="flex flex-col gap-3 w-full">
                <Button 
                  onClick={checkPaymentStatus}
                  className="w-full py-5 bg-amber-500 hover:bg-amber-600 text-white font-bold"
                >
                  <RefreshCw className="mr-2 w-4 h-4" /> {t('paymentResult.recheck')}
                </Button>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Button variant="outline" onClick={() => navigate('/subscription')}>
                    {t('payment.backToPlans')}
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/profile')}>
                    {t('paymentResult.profile')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  </div>
</div>
  );
};

export default PaymentResultPage;
